import { Buffer } from "buffer"
import dayjs from "dayjs"

type CustomerType = {
  customerToken: string
  loginToken: string
}

type configVariblesType = {
  credoraxMerchantID: string
  credoraxStaticKey: string
  credoraxStoreURL: string
}

interface CreditCardFormI {
  cc: string
  ccv: string
  name: string
  month: string
  year: string
}

export default class CredoraxClass {
  private customer: CustomerType
  private creditCardAPIUrl: string
  private apiurl: string
  private configVaribles: configVariblesType

  constructor(
    customer: CustomerType,
    creditCardAPIUrl: string,
    apiurl: string,
  ) {
    this.customer = customer
    this.creditCardAPIUrl = creditCardAPIUrl
    this.apiurl = apiurl
    this.configVaribles = {
      credoraxMerchantID: "",
      credoraxStaticKey: "",
      credoraxStoreURL: "",
    }
  }

  async submitCreditCard({
    cc,
    ccv,
    name,
    month,
    year,
  }: CreditCardFormI): Promise<
    | {
        threeDSMethodData: string
        url3dSecure: any
        threedsrxid: any
        pKey: any
        form: any
      }
    | boolean
    | unknown
  > {
    const res = await this.credoraxCCVerify({ cc, ccv, name, month, year })
    const data = res

    const maskCC = this.generateMaskCC(cc)
    if (data?.PKey && data.z2 === "0") {
      const form = {
        ccToken: data.PKey,
        customer: this.customer,
        ccDate: month.toString() + year.toString(),
        ccMask: maskCC,
        name: name,
        z2: data.z2,
      }

      localStorage.setItem("ccMask", maskCC)
      localStorage.setItem("ccDate", month.toString() + year.toString())

      const pKey = data.PKey
      const url3dSecure = data["3ds_method"]

      const threeDData = {
        threeDSMethodNotificationURL: `${this.apiurl}/MagicRegistration/ThreeDsCallback`,
        threeDSServerTransID: data["3ds_trxid"],
      }
      const threedsrxid = data["3ds_trxid"]

      let objJsonStr = JSON.stringify(threeDData)

      let threeDSMethodData = Buffer.from(objJsonStr).toString("base64")

      return {
        threeDSMethodData,
        url3dSecure,
        threedsrxid,
        pKey,
        form,
      }
    }

    return data
  }

  private async credoraxCCVerify({
    cc,
    ccv,
    name,
    month,
    year,
  }: CreditCardFormI) {
    const formData = new FormData()

    const configVaribles = await this.GetCredoraxDetailsResultResponse()
    this.configVaribles = configVaribles

    formData.append("M", configVaribles?.credoraxMerchantID)
    formData.append("RequestID", Math.random().toString(36).slice(2))
    formData.append("Statickey", configVaribles?.credoraxStaticKey)
    formData.append("b1", cc)
    formData.append("b3", month)
    formData.append("b4", year)
    formData.append("b5", ccv)
    formData.append("c1", name)

    const res = await fetch(this.creditCardAPIUrl, {
      method: "POST",
      body: formData,
    })

    return res.json()
  }

  private generateMaskCC(cc: string): string {
    let maskCC = cc.replaceAll(" ", "")
    maskCC = `${maskCC.substring(0, 5)}...${maskCC.substring(
      maskCC.length - 4,
      maskCC.length,
    )}`

    return maskCC
  }

  private GetCredoraxDetailsResultResponse = async () => {
    const response = await fetch(
      `${this.apiurl}MagicRegistration/GetCredoraxDetails`,
      {
        headers: {
          "X-Token": this.customer.loginToken,
        },
        method: "get",
        body: null,
      },
    )
    return response.json()
  }

  private generateCredoraxToken = async (
    registrationUrl: string,
    threeDsCompind: boolean,
    pKey: string,
    window: any,
    holderName: string,
  ) => {
    const formatDate = "YYYYMMDDHHmmss"
    const constpurchasedate = new Date()
    let constpurchasedateFormated = dayjs(constpurchasedate).format(formatDate)
    const a1 = Math.random().toString(36).slice(2)

    let customerToken = this.customer.customerToken
    let a1h9 = customerToken.split("_")[1] + "_" + a1

    const screenHeight = window.height.replace(/\D/g, "")
    const screenWidth = window.width.replace(/\D/g, "")

    let threedsCompindAnswer = threeDsCompind ? "Y" : "N"
    const c1 = holderName
    const c3 = ""

    const purchasedate = constpurchasedateFormated
      ? constpurchasedateFormated.toString()
      : ""

    const parameters: any = {
      _3ds_browseracceptheader: "",
      _3ds_browsercolordepth: "32",
      _3ds_browserjavaenabled: "false",
      _3ds_browsertz: "60",
      _3ds_category: "01",
      _3ds_challengewindowsize: "01",
      _3ds_channel: "02",
      _3ds_initiate: "01",
      _3ds_transtype: "01",
      O: "10",
      a4: "0",
      a9: "6",
      a5: "EUR",
      d6: "en-US",
      _3ds_compind: threedsCompindAnswer,
      PKey: pKey,
      c1: c1,
      c3: c3,
      d2: c1,
      h9: a1h9,
      _3ds_browserscreenheight: screenHeight,
      _3ds_browserscreenwidth: screenWidth,
      M: this.configVaribles.credoraxMerchantID,
      a1: a1,
      _3ds_redirect_url: registrationUrl,
      _3ds_purchasedate: purchasedate,
    }

    parameters["_CreditCardActionSourceTypeId"] = 2

    parameters["_RegistrationSourceTypeId"] = 1

    const response = await fetch(
      `${this.apiurl}MagicRegistration/PaymentRequest`,
      {
        method: "POST",
        headers: {
          "X-Token": this.customer.loginToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(parameters),
      },
    )

    return response.json()
  }

  getThreeDsResult = async (threedsrxid: any) => {
    const response = await fetch(
      `${this.apiurl}/MagicRegistration/GetThreeDsResult?pKey=${threedsrxid}`,
      {
        headers: { "Content-Type": "application/json" },
        method: "get",
        body: null,
      },
    )
    return response.json()
  }

  finishCreditAdd = async (
    ccToken: string,
    ccMask: string,
    ccValidityDate: string,
    CcAuthNum: string,
    CCCompanyTypeId: string,
  ) => {
    const requestData = {
      customer: this.customer,
      ccToken: ccToken,
      ccMask: ccMask,
      ccValidityDate: ccValidityDate,
      CcAuthNum: CcAuthNum,
      CCCompanyTypeId: CCCompanyTypeId,
    }

    const response = await fetch(
      `${this.apiurl}MagicRegistration/PaymentRequest`,
      {
        method: "POST",
        headers: {
          "X-Token": this.customer.loginToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      },
    )
  }

  async updateUserWithNewCreditCard({
    ccToken,
    CcAuthNum,
    CCCompanyTypeId,
    accountId,
  }: any) {
    const ccMask = localStorage.getItem("ccMask") || ""
    const requestData = {
      personal: { customerToken: this.customer.customerToken },
      accountDetails: {
        accountId,
        creditCardList: [
          {
            CCCompanyTypeId: CCCompanyTypeId,
            ccMask: ccMask,
            CreditAuthNum: CcAuthNum,
            CreditCardToken: ccToken,
            creditValidity: localStorage.getItem("ccDate"),
            last4Digits: ccMask.split("...")[1],
            isDefault: true,
            isActive: true,
          },
        ],
      },
    }

    const res = await fetch(`${this.apiurl}GetUser/Update`, {
      method: "PUT",
      body: JSON.stringify(requestData),
      headers: {
        "X-Token": this.customer.loginToken,
        "X-Customer": this.customer.customerToken,
        "Content-Type": "application/json",
      },
    })

    localStorage.removeItem("ccMask")
    localStorage.removeItem("ccDate")
    return res.json()
  }

  async updateCreditCard({
    paymentMethodId,
    accountId,
    isActive = null,
    isDefault = null,
  }: {
    paymentMethodId: number
    accountId: number | null
    isActive?: boolean | null
    isDefault?: boolean | null
  }) {
    let requestData

    if (isActive !== null) {
      requestData = {
        personal: { customerToken: this.customer.customerToken },
        accountDetails: {
          accountId,
          creditCardList: [
            {
              paymentMethodId,
              isActive,
            },
          ],
        },
      }
    }

    if (isDefault !== null) {
      requestData = {
        personal: { customerToken: this.customer.customerToken },
        accountDetails: {
          accountId,
          creditCardList: [
            {
              paymentMethodId,
              isDefault,
            },
          ],
        },
      }
    }

    const res = await fetch(`${this.apiurl}GetUser/Update`, {
      method: "PUT",
      body: JSON.stringify(requestData),
      headers: {
        "X-Token": this.customer.loginToken,
        "X-Customer": this.customer.customerToken,
        "Content-Type": "application/json",
      },
    })

    return res.json()
  }
}
